"use server";

import { createClient, createAdminClient } from "@/lib/supabase/server";
import { auth } from "@clerk/nextjs/server";
import { reportUsage } from "@/lib/supabase/stripe-actions";
import type {
  Employee,
  EmployeeWithRelations,
  Department,
  CreateEmployeeInput,
  UpdateEmployeeInput,
  EmployeeStatus,
  EmploymentType,
  PayFrequency,
} from "./employees-types";

// ── Helpers ──────────────────────────────────────────────────

async function getOrganisationId(): Promise<string> {
  const { userId } = await auth();
  if (!userId) throw new Error("Unauthenticated");

  const supabase = await createAdminClient();
  const { data: member, error } = await supabase
    .from("organisation_members")
    .select("organisation_id")
    .eq("user_id", userId)
    .single();

  if (error || !member) throw new Error("No organisation found");
  return member.organisation_id;
}

// ── Fetch functions ──────────────────────────────────────────

export async function fetchEmployees(): Promise<EmployeeWithRelations[]> {
  const orgId = await getOrganisationId();
  const supabase = await createAdminClient();

  const { data, error } = await supabase
    .from("employees")
    .select(
      `
      *,
      department:departments(id, name),
      manager:employees!manager_id(first_name, last_name)
    `,
    )
    .eq("organisation_id", orgId)
    .order("first_name", { ascending: true });

  if (error) throw error;

  return (data || []).map((emp: any) => ({
    ...emp,
    department: emp.department
      ? ({
          id: emp.department.id,
          name: emp.department.name,
          organisation_id: orgId,
          created_at: "",
        } as Department)
      : null,
    manager: emp.manager
      ? {
          first_name: emp.manager.first_name,
          last_name: emp.manager.last_name,
        }
      : null,
  })) as EmployeeWithRelations[];
}

export async function fetchEmployeeDetail(
  employeeId: string,
): Promise<EmployeeWithRelations | null> {
  const orgId = await getOrganisationId();
  const supabase = await createAdminClient();

  const { data, error } = await supabase
    .from("employees")
    .select(
      `
      *,
      department:departments(id, name),
      manager:employees!manager_id(first_name, last_name)
    `,
    )
    .eq("id", employeeId)
    .eq("organisation_id", orgId)
    .single();

  if (error || !data) return null;

  return {
    ...data,
    department: data.department
      ? ({
          id: data.department.id,
          name: data.department.name,
          organisation_id: orgId,
          created_at: "",
        } as Department)
      : null,
    manager: data.manager
      ? {
          first_name: data.manager.first_name,
          last_name: data.manager.last_name,
        }
      : null,
  } as EmployeeWithRelations;
}

export async function fetchDepartments(): Promise<Department[]> {
  const orgId = await getOrganisationId();
  const supabase = await createAdminClient();

  const { data, error } = await supabase
    .from("departments")
    .select("*")
    .eq("organisation_id", orgId)
    .order("name", { ascending: true });

  if (error) throw error;
  return (data || []) as Department[];
}

export async function fetchManagerDropdown(): Promise<
  { id: string; first_name: string; last_name: string }[]
> {
  const orgId = await getOrganisationId();
  const supabase = await createAdminClient();

  const { data, error } = await supabase
    .from("employees")
    .select("id, first_name, last_name")
    .eq("organisation_id", orgId)
    .eq("status", "active")
    .order("first_name", { ascending: true });

  if (error) throw error;
  return (data || []) as any[];
}

// ── Server actions (writes) ──────────────────────────────────

export async function createEmployee(
  input: CreateEmployeeInput,
): Promise<{ success: boolean; employeeId?: string; error?: string }> {
  const { userId } = await auth();
  if (!userId) return { success: false, error: "Unauthenticated" };

  const orgId = await getOrganisationId();
  const supabase = await createAdminClient();

  const { data, error } = await supabase
    .from("employees")
    .insert({
      organisation_id: orgId,
      first_name: input.first_name,
      last_name: input.last_name,
      email: input.email,
      phone: input.phone || null,
      date_of_birth: input.date_of_birth || null,
      address: input.address || null,
      job_title: input.job_title || null,
      department_id: input.department_id || null,
      employment_type: input.employment_type,
      start_date: input.start_date || null,
      salary: input.salary || null,
      hourly_rate: input.hourly_rate || null,
      pay_frequency: input.pay_frequency,
      manager_id: input.manager_id || null,
    })
    .select("id")
    .single();

  if (error || !data) {
    return {
      success: false,
      error: error?.message || "Failed to create employee",
    };
  }

  // Report usage for PAYG billing (non-blocking)
  reportUsage("employees", 1).catch(() => {});

  return { success: true, employeeId: data.id };
}

export async function updateEmployee(
  employeeId: string,
  input: UpdateEmployeeInput,
): Promise<{ success: boolean; error?: string }> {
  const orgId = await getOrganisationId();
  const supabase = await createAdminClient();

  const updateData: Record<string, any> = {};
  if (input.first_name !== undefined) updateData.first_name = input.first_name;
  if (input.last_name !== undefined) updateData.last_name = input.last_name;
  if (input.email !== undefined) updateData.email = input.email;
  if (input.phone !== undefined) updateData.phone = input.phone;
  if (input.date_of_birth !== undefined)
    updateData.date_of_birth = input.date_of_birth;
  if (input.address !== undefined) updateData.address = input.address;
  if (input.job_title !== undefined) updateData.job_title = input.job_title;
  if (input.department_id !== undefined)
    updateData.department_id = input.department_id;
  if (input.employment_type !== undefined)
    updateData.employment_type = input.employment_type;
  if (input.start_date !== undefined) updateData.start_date = input.start_date;
  if (input.end_date !== undefined) updateData.end_date = input.end_date;
  if (input.salary !== undefined) updateData.salary = input.salary;
  if (input.hourly_rate !== undefined)
    updateData.hourly_rate = input.hourly_rate;
  if (input.pay_frequency !== undefined)
    updateData.pay_frequency = input.pay_frequency;
  if (input.status !== undefined) updateData.status = input.status;
  if (input.manager_id !== undefined) updateData.manager_id = input.manager_id;

  const { error } = await supabase
    .from("employees")
    .update(updateData)
    .eq("id", employeeId)
    .eq("organisation_id", orgId);

  if (error) return { success: false, error: error.message };
  return { success: true };
}

export async function updateEmployeeStatus(
  employeeId: string,
  status: EmployeeStatus,
): Promise<{ success: boolean; error?: string }> {
  return updateEmployee(employeeId, { status });
}

// ── Onboarding actions ───────────────────────────────────────

export async function setupOrganisation(
  name: string,
  slug: string,
  country: string,
): Promise<{ success: boolean; organisationId?: string; error?: string }> {
  const { userId } = await auth();
  if (!userId) return { success: false, error: "Unauthenticated" };

  const supabaseAdmin = await createAdminClient();

  // Insert profile first (required by foreign key constraint)
  const { error: profileError } = await supabaseAdmin
    .from("profiles")
    .upsert({ id: userId }, { onConflict: "id" });

  if (profileError) {
    return {
      success: false,
      error: `Failed to create profile: ${profileError.message}`,
    };
  }

  // Insert organisation
  const { data: org, error: orgError } = await supabaseAdmin
    .from("organisations")
    .insert({ name, slug })
    .select("id")
    .single();

  if (orgError) {
    if (orgError.code === "23505" && orgError.details?.includes("slug")) {
      return {
        success: false,
        error: `The workspace name "${slug}" is already taken. Please choose a different slug.`,
      };
    }
    return {
      success: false,
      error: orgError.message || "Failed to create organisation",
    };
  }

  if (!org) {
    return { success: false, error: "Failed to create organisation" };
  }

  // Insert member as owner
  const { error: memberError } = await supabaseAdmin
    .from("organisation_members")
    .insert({ organisation_id: org.id, user_id: userId, role: "owner" });

  if (memberError) {
    return { success: false, error: memberError.message };
  }

  return { success: true, organisationId: org.id };
}

export async function createInvitations(
  emails: string[],
  organisationId: string,
): Promise<{ success: boolean; error?: string }> {
  const { userId } = await auth();
  if (!userId) return { success: false, error: "Unauthenticated" };

  const supabase = await createAdminClient();

  const invitations = emails.map((email) => ({
    organisation_id: organisationId,
    email,
    invited_by: userId,
  }));

  if (invitations.length > 0) {
    const { error } = await supabase
      .from("pending_invitations")
      .insert(invitations);

    if (error) return { success: false, error: error.message };
  }

  return { success: true };
}

// ── Join by token ────────────────────────────────────────────

export async function fetchInvitationByToken(
  token: string,
): Promise<{ orgName: string; email: string } | null> {
  const supabase = await createAdminClient();

  const { data, error } = await supabase
    .from("pending_invitations")
    .select(
      `
      email,
      organisations(name),
      expires_at,
      status
    `,
    )
    .eq("token", token)
    .single();

  if (error || !data) return null;
  if (data.status === "expired") return null;
  if (new Date(data.expires_at) < new Date()) return null;

  return {
    orgName: (data.organisations as any)?.name || "Unknown",
    email: data.email,
  };
}

export async function acceptInvitation(
  token: string,
): Promise<{ success: boolean; error?: string }> {
  const { userId } = await auth();
  if (!userId) return { success: false, error: "Unauthenticated" };

  const supabase = await createAdminClient();
  const supabaseAdmin = await createAdminClient();

  // Fetch the invitation
  const { data: invitation, error: invError } = await supabase
    .from("pending_invitations")
    .select("organisation_id, email, status, expires_at")
    .eq("token", token)
    .single();

  if (invError || !invitation)
    return { success: false, error: "Invitation not found" };
  if (invitation.status === "accepted")
    return { success: false, error: "Invitation already used" };
  if (new Date(invitation.expires_at) < new Date())
    return { success: false, error: "Invitation expired" };

  // Add user to organisation (using admin to bypass RLS)
  const { error: memberError } = await supabaseAdmin
    .from("organisation_members")
    .insert({
      organisation_id: invitation.organisation_id,
      user_id: userId,
      role: "employee",
    });

  if (memberError && memberError.code !== "23505") {
    // 23505 = unique violation (already a member), which is ok
    return { success: false, error: memberError.message };
  }

  // Mark invitation as accepted
  await supabase
    .from("pending_invitations")
    .update({ status: "accepted" })
    .eq("token", token);

  return { success: true };
}

export async function processInvitationToken(
  token: string,
): Promise<{ success: boolean; redirectUrl?: string; error?: string }> {
  const { userId } = await auth();
  if (!userId) return { success: false, error: "Unauthenticated" };

  const supabase = await createAdminClient();

  const { data: invitation, error: invError } = await supabase
    .from("pending_invitations")
    .select("organisation_id, email, status, expires_at, organisations(name)")
    .eq("token", token)
    .single();

  if (invError || !invitation)
    return { success: false, error: "Invitation not found" };
  if (invitation.status === "accepted")
    return { success: false, error: "Already accepted" };
  if (new Date(invitation.expires_at) < new Date())
    return { success: false, error: "Expired" };

  const { error: memberError } = await supabase
    .from("organisation_members")
    .insert({
      organisation_id: invitation.organisation_id,
      user_id: userId,
      role: "employee",
    });

  if (memberError && memberError.code !== "23505") {
    return { success: false, error: memberError.message };
  }

  await supabase
    .from("pending_invitations")
    .update({ status: "accepted" })
    .eq("token", token);

  return { success: true, redirectUrl: "/dashboard" };
}

/**
 * Check if the current user's email has any pending invitations.
 * Returns the most recent one if found.
 */
export async function checkPendingInvitation(): Promise<{
  orgName: string;
  orgId: string;
  invitationId: string;
} | null> {
  const { userId, sessionClaims } = await auth();
  if (!userId) return null;

  // Get the user's email from Clerk
  const userEmail = (sessionClaims as any)?.email;
  if (!userEmail) return null;

  const supabase = await createAdminClient();

  const { data, error } = await supabase
    .from("pending_invitations")
    .select("id, organisation_id, organisations(name)")
    .eq("email", userEmail)
    .eq("status", "pending")
    .gte("expires_at", new Date().toISOString())
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;

  return {
    orgName: (data.organisations as any)?.name || "Unknown",
    orgId: data.organisation_id,
    invitationId: data.id,
  };
}

/**
 * Accept a pending invitation by organisation ID (for the current user's email).
 */
export async function acceptInvitationByOrg(
  orgId: string,
): Promise<{ success: boolean; error?: string }> {
  const { userId, sessionClaims } = await auth();
  if (!userId) return { success: false, error: "Unauthenticated" };

  const userEmail = (sessionClaims as any)?.email;
  if (!userEmail) return { success: false, error: "Email not found" };

  const supabase = await createAdminClient();
  const supabaseAdmin = await createAdminClient();

  // Mark matching invitation as accepted
  await supabase
    .from("pending_invitations")
    .update({ status: "accepted" })
    .eq("organisation_id", orgId)
    .eq("email", userEmail)
    .eq("status", "pending");

  // Add user to organisation (using admin to bypass RLS)
  const { error: memberError } = await supabaseAdmin
    .from("organisation_members")
    .insert({
      organisation_id: orgId,
      user_id: userId,
      role: "employee",
    });

  if (memberError && memberError.code !== "23505") {
    return { success: false, error: memberError.message };
  }

  return { success: true };
}

/**
 * Join an organisation by its join code.
 */
export async function joinOrganisationByCode(
  code: string,
): Promise<{ success: boolean; orgName?: string; error?: string }> {
  const { userId } = await auth();
  if (!userId) return { success: false, error: "Unauthenticated" };

  const supabase = await createAdminClient();
  const supabaseAdmin = await createAdminClient();

  const { data: org, error: orgError } = await supabase
    .from("organisations")
    .select("id, name")
    .eq("join_code", code.toUpperCase().trim())
    .single();

  if (orgError || !org) return { success: false, error: "Invalid join code" };

  // Add user to organisation (using admin to bypass RLS)
  const { error: memberError } = await supabaseAdmin
    .from("organisation_members")
    .insert({ organisation_id: org.id, user_id: userId, role: "employee" });

  if (memberError && memberError.code !== "23505") {
    return { success: false, error: memberError.message };
  }

  return { success: true, orgName: org.name };
}
